/**
 * Telegram WebApp utilities
 */

declare global {
  interface Window {
    Telegram?: {
      WebApp: {
        ready: () => void;
        expand: () => void;
        close: () => void;
        initData: string;
        initDataUnsafe: {
          user?: {
            id: number;
            first_name: string;
            last_name?: string;
            username?: string;
            language_code?: string;
            is_premium?: boolean;
            photo_url?: string;
          };
          query_id?: string;
          auth_date?: number;
          hash?: string;
        };
        themeParams: {
          bg_color?: string;
          text_color?: string;
          hint_color?: string;
          link_color?: string;
          button_color?: string;
          button_text_color?: string;
          secondary_bg_color?: string;
        };
        colorScheme: 'light' | 'dark';
        isExpanded: boolean;
        viewportHeight: number;
        viewportStableHeight: number;
        headerColor: string;
        backgroundColor: string;
        setHeaderColor: (color: string) => void;
        setBackgroundColor: (color: string) => void;
        enableClosingConfirmation: () => void;
        disableClosingConfirmation: () => void;
        HapticFeedback: {
          impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
          notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
          selectionChanged: () => void;
        };
        MainButton: {
          text: string;
          color: string;
          textColor: string;
          isVisible: boolean;
          isActive: boolean;
          isProgressVisible: boolean;
          setText: (text: string) => void;
          onClick: (callback: () => void) => void;
          offClick: (callback: () => void) => void;
          show: () => void;
          hide: () => void;
          enable: () => void;
          disable: () => void;
          showProgress: (leaveActive?: boolean) => void;
          hideProgress: () => void;
        };
        BackButton: {
          isVisible: boolean;
          onClick: (callback: () => void) => void;
          offClick: (callback: () => void) => void;
          show: () => void;
          hide: () => void;
        };
      };
    };
  }
}

/**
 * Get Telegram WebApp instance
 */
export const getTelegramWebApp = () => {
  return window.Telegram?.WebApp;
};

/**
 * Check if running in Telegram WebApp
 */
export const isTelegramWebApp = (): boolean => {
  return !!window.Telegram?.WebApp;
};

/**
 * Initialize Telegram WebApp
 */
export const initTelegramWebApp = () => {
  const tg = getTelegramWebApp();
  if (tg) {
    tg.ready();
    tg.expand();
  }
};

/**
 * Haptic feedback helpers
 */
export const haptic = {
  impact: (style: 'light' | 'medium' | 'heavy' = 'medium') => {
    getTelegramWebApp()?.HapticFeedback.impactOccurred(style);
  },
  notification: (type: 'error' | 'success' | 'warning') => {
    getTelegramWebApp()?.HapticFeedback.notificationOccurred(type);
  },
  selection: () => {
    getTelegramWebApp()?.HapticFeedback.selectionChanged();
  },
};

/**
 * Get Telegram theme colors
 */
export const getTelegramTheme = () => {
  const tg = getTelegramWebApp();
  return {
    colorScheme: tg?.colorScheme || 'light',
    colors: tg?.themeParams || {},
  };
};

/**
 * Get current Telegram user
 */
export const getTelegramUser = () => {
  return getTelegramWebApp()?.initDataUnsafe.user;
};

/**
 * Get Telegram init data for authentication
 */
export const getTelegramInitData = (): string | undefined => {
  return getTelegramWebApp()?.initData;
};
